import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { FaFileDownload } from "react-icons/fa";

import { axiosGet } from "api";
import CustomGrid from "components/lists/CustomGrid";
import Loader from "components/lists/Loader";
import PaginationBar from "components/utils/PaginationBar";
import { Roles, STATUS_CODES } from "constants/index";
import {
  ARTICLE_LOCATIONS_BY_DATE_EXPORT_URL,
  ARTICLE_LOCATIONS_BY_DATE_URL,
} from "constants/urls";
import { buildExcelHeader } from "helpers/exportToExcel";
import { queryBuilder } from "helpers/query";
import {
  exportGroupedStockToExcel,
  exportGroupedStockToPdf,
  exportStockToExcel,
  exportStockToPdf,
  StockExportColumn,
} from "helpers/stockExports";
import { useToast } from "hooks/useToast";
import { useSession } from "providers/SessionProvider";
import {
  ArticleLocationStockByDate,
  ArticleLocationStockByDateExportResponse,
  ArticleLocationStockByDateResponse,
  Client,
  StockExportGroupBy,
} from "types";
import StockExportGroupingModal from "./StockExportGroupingModal";

type LocationOption = { id: number; label: string };

type StockByDateFilters = {
  storeIds: number[];
  locationFrom?: LocationOption;
  locationTo?: LocationOption;
  locationIds: number[];
  clientIds: number[];
  lineIds: number[];
  articleCode: string;
  articleDescription: string;
  lote: string;
  oc: string;
  amount_per_container: string;
  fecha_vencimiento: string;
  fecha_ingreso: string;
  groupId?: number;
  movementId: string;
};

const initialFilters: StockByDateFilters = {
  storeIds: [],
  locationIds: [],
  clientIds: [],
  lineIds: [],
  articleCode: "",
  articleDescription: "",
  lote: "",
  oc: "",
  amount_per_container: "",
  fecha_vencimiento: "",
  fecha_ingreso: "",
  movementId: "",
};

const PAGE_SIZE = 25;

const initialFilterOptions = {
  lotes: [] as string[],
  ocs: [] as string[],
  amounts_per_container: [] as number[],
  expiration_dates: [] as string[],
  income_dates: [] as string[],
};

const FilterItem = ({ children }: { children: ReactNode }) => (
  <Grid item md={3} sm={6} xs={12}>
    {children}
  </Grid>
);

const getHistoricalStockBalance = (row: ArticleLocationStockByDate) =>
  row.saldo ??
  Number(row.stock ?? 0) * Number(row.amount_per_container ?? 0);

export default function StockByDateList() {
  const { stores, clients, locations, articles, groups, userData } =
    useSession();
  const { showSnackBar } = useToast();
  const isClientUser = userData?.role === Roles.CLIENT;

  const [stockDate, setStockDate] = useState<Date>(new Date());
  const [filters, setFilters] = useState<StockByDateFilters>(initialFilters);
  const [movementIdInput, setMovementIdInput] = useState("");
  const [articleDescriptionInput, setArticleDescriptionInput] = useState("");
  const [rows, setRows] = useState<ArticleLocationStockByDate[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [filterOptions, setFilterOptions] = useState(initialFilterOptions);
  const [loading, setLoading] = useState(false);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    [],
  );
  const [selectedClients, setSelectedClients] = useState<Client[]>([]);
  const [selectedLineClients, setSelectedLineClients] = useState<Client[]>([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [pendingExportFormat, setPendingExportFormat] = useState<
    "excel" | "pdf" | null
  >(null);
  const requestIdRef = useRef(0);

  const queryClientIds = useMemo(
    () => (filters.lineIds.length > 0 ? filters.lineIds : filters.clientIds),
    [filters.clientIds, filters.lineIds],
  );

  const scopedClientIds = useMemo(
    () => [
      ...queryClientIds,
      ...clients
        .filter((client) =>
          queryClientIds.includes(client.parent_client_id ?? 0),
        )
        .map((client) => client.id),
    ],
    [clients, queryClientIds],
  );

  const lineOptions = useMemo(() => {
    if (isClientUser) {
      const assignedClient = clients.find(
        (client) => client.id === userData?.client_id,
      );
      if (!assignedClient || assignedClient.parent_client_id != null) return [];
      return (assignedClient.child_clients ?? []).filter(
        (client) => client.is_active,
      );
    }

    return clients.filter(
      (client) =>
        client.is_active &&
        client.parent_client_id != null &&
        filters.clientIds.includes(client.parent_client_id),
    );
  }, [clients, filters.clientIds, isClientUser, userData?.client_id]);

  const uniqueArticles = useMemo(() => {
    const scopedArticles =
      queryClientIds.length > 0
        ? articles.filter((article) =>
            scopedClientIds.includes(article.client_id),
          )
        : articles;

    return Array.from(
      new Map(scopedArticles.map((article) => [article.code, article])).values(),
    );
  }, [articles, queryClientIds, scopedClientIds]);

  const groupOptions = useMemo(
    () =>
      groups.filter(
        (group) =>
          queryClientIds.length === 0 ||
          scopedClientIds.includes(group.client_id || 0),
      ),
    [groups, queryClientIds, scopedClientIds],
  );

  const filterVisibility = useMemo(() => {
    if (selectedClients.length === 0) {
      return {
        show_lote: true,
        show_oc: true,
        show_fecha_vencimiento: true,
      };
    }

    return {
      show_lote: selectedClients.some((client) => client.show_lote),
      show_oc: selectedClients.some((client) => client.show_oc),
      show_fecha_vencimiento: selectedClients.some(
        (client) => client.show_fecha_vencimiento,
      ),
    };
  }, [selectedClients]);

  const query = useMemo(
    () =>
      queryBuilder({
        date: dayjs(stockDate).format("YYYY-MM-DD"),
        storeIds: filters.storeIds,
        locationIds: filters.locationIds,
        clientIds: queryClientIds,
        articleCode: filters.articleCode || undefined,
        articleDescription: filters.articleDescription.trim() || undefined,
        movementId: filters.movementId || undefined,
        lote: filters.lote || undefined,
        oc: filters.oc || undefined,
        amount_per_container: filters.amount_per_container || undefined,
        fecha_vencimiento: filters.fecha_vencimiento || undefined,
        fecha_ingreso: filters.fecha_ingreso || undefined,
        groupId: filters.groupId,
      }),
    [filters, queryClientIds, stockDate],
  );

  const paginatedQuery = useMemo(() => {
    const separator = query.includes("?") ? "&" : "?";
    return `${query}${separator}page=${page}&limit=${PAGE_SIZE}&includeMetadata=${page === 1}`;
  }, [page, query]);
  const [debouncedQuery, setDebouncedQuery] = useState(paginatedQuery);

  useEffect(() => {
    setPage(1);
  }, [query]);

  useEffect(() => {
    requestIdRef.current += 1;
    const timeoutId = window.setTimeout(
      () => setDebouncedQuery(paginatedQuery),
      300,
    );

    return () => window.clearTimeout(timeoutId);
  }, [paginatedQuery]);

  useEffect(() => {
    const requestId = ++requestIdRef.current;

    const fetchRows = async () => {
      setLoading(true);
      const response = await axiosGet(
        `${ARTICLE_LOCATIONS_BY_DATE_URL}${debouncedQuery}`,
        true,
      );

      if (requestId !== requestIdRef.current) return;

      if (response.status === STATUS_CODES.OK) {
        const data = response.data as ArticleLocationStockByDateResponse;
        setRows(data.result ?? []);
        if (data.count !== undefined) setTotalCount(data.count);
        if (data.filter_options) setFilterOptions(data.filter_options);
      } else {
        setRows([]);
        setTotalCount(0);
        setFilterOptions(initialFilterOptions);
        showSnackBar(
          response.data?.message ?? "No se pudo obtener el stock por fecha.",
          "error",
        );
      }

      setLoading(false);
    };

    void fetchRows();
  }, [debouncedQuery, showSnackBar]);

  useEffect(() => {
    setFilters((current) => ({
      ...current,
      groupId: undefined,
      articleCode: "",
    }));
  }, [queryClientIds]);

  useEffect(() => {
    if (!filters.locationFrom || filters.locationTo) return;

    const storeLocations = locations;
    const fromIndex = storeLocations.findIndex(
      (location) => location.id === filters.locationFrom?.id,
    );
    if (fromIndex === -1) return;

    setFilters((current) => ({
      ...current,
      locationIds: storeLocations.slice(fromIndex).map((location) => location.id),
    }));
  }, [filters.locationFrom, filters.locationTo, filters.storeIds, locations]);

  const structure = useMemo(() => {
    const fields: GridColDef[] = [];

    if (!isClientUser) {
      fields.push(
        {
          field: "deposito",
          headerName: "Depósito",
          minWidth: 160,
          flex: 1,
          valueGetter: (params) =>
            params.row.location?.store?.description ?? "-",
        },
        {
          field: "ubicacion",
          headerName: "Ubicación",
          minWidth: 140,
          flex: 1,
          valueGetter: (params) => params.row.location?.code ?? "-",
        },
      );
    }

    fields.push(
      {
        field: "codigo",
        headerName: "Código",
        minWidth: 120,
        flex: 1,
        valueGetter: (params) => params.row.article?.code ?? "-",
      },
      {
        field: "descripcion",
        headerName: "Descripción",
        minWidth: 240,
        flex: 1.5,
        valueGetter: (params) => params.row.article?.description ?? "-",
      },
      {
        field: "cliente",
        headerName: "Cliente",
        minWidth: 140,
        flex: 0.8,
        valueGetter: (params) => params.row.article?.client?.code ?? "-",
      },
    );

    if (filterVisibility.show_lote) {
      fields.push({
        field: "lote",
        headerName: "Lote",
        minWidth: 140,
        flex: 1,
        valueGetter: (params) => params.row.lote ?? "-",
      });
    }
    if (filterVisibility.show_oc) {
      fields.push({
        field: "oc",
        headerName: "OC",
        minWidth: 120,
        flex: 1,
        valueGetter: (params) => params.row.oc ?? "-",
      });
    }
    if (filterVisibility.show_fecha_vencimiento) {
      fields.push({
        field: "fecha_vencimiento",
        headerName: "Fecha Vencimiento",
        minWidth: 180,
        flex: 1,
        valueGetter: (params) => params.row.fecha_vencimiento ?? "-",
      });
    }

    fields.push(
      {
        field: "fecha_ingreso",
        headerName: "Fecha Ingreso",
        minWidth: 150,
        flex: 1,
        valueGetter: (params) => params.row.fecha_ingreso ?? "-",
      },
      {
        field: "stock",
        headerName: "Cant.",
        minWidth: 100,
        flex: 0.6,
        valueGetter: (params) => params.row.stock ?? 0,
      },
      {
        field: "cantidad_contenedor",
        headerName: "Cant. por Contenedor",
        minWidth: 190,
        flex: 1,
        valueGetter: (params) => params.row.amount_per_container ?? "-",
      },
      {
        field: "saldo",
        headerName: "Saldo",
        minWidth: 120,
        flex: 0.6,
        valueGetter: (params) => getHistoricalStockBalance(params.row),
      },
      {
        field: "unidad",
        headerName: "Unidad de Medida",
        minWidth: 170,
        flex: 1,
        valueGetter: (params) => params.row.article?.unit?.description ?? "-",
      },
    );

    return {
      name: "articleLocationsByDate",
      labelName: "Stock por Fecha",
      fields,
    };
  }, [filterVisibility, isClientUser]);

  const exportColumns = useMemo(() => {
    const columns: StockExportColumn<ArticleLocationStockByDate>[] = [];

    if (!isClientUser) {
      columns.push(
        {
          id: "store",
          header: "Depósito",
          value: (row) => row.location?.store?.description ?? "-",
          pdfCellWidth: 15,
        },
        {
          id: "location",
          header: "Ubicación.",
          value: (row) => row.location?.code ?? "-",
          pdfCellWidth: 14,
        },
      );
    }

    columns.push(
      {
        id: "article",
        header: "Código",
        value: (row) => row.article?.code ?? "-",
        pdfCellWidth: 20,
      },
      {
        id: "description",
        header: "Desc.",
        value: (row) => row.article?.description ?? "-",
        pdfCellWidth: 32,
      },
      {
        id: "client",
        header: "Cliente",
        value: (row) => row.article?.client?.code ?? "-",
        pdfCellWidth: 10,
      },
    );

    if (filterVisibility.show_lote) {
      columns.push({
        id: "lote",
        header: "Lote",
        value: (row) => row.lote ?? "-",
        pdfCellWidth: 18,
      });
    }
    if (filterVisibility.show_oc) {
      columns.push({
        id: "oc",
        header: "OC",
        value: (row) => row.oc ?? "-",
        pdfCellWidth: 16,
      });
    }
    if (filterVisibility.show_fecha_vencimiento) {
      columns.push({
        id: "fecha_vencimiento",
        header: "F. Venc.",
        value: (row) => row.fecha_vencimiento ?? "-",
        pdfCellWidth: 16,
      });
    }

    columns.push(
      {
        id: "fecha_ingreso",
        header: "F. Ing.",
        value: (row) => row.fecha_ingreso ?? "-",
        pdfCellWidth: 16,
      },
      {
        id: "quantity",
        header: "Cant.",
        value: (row) => row.stock,
        pdfCellWidth: 10,
      },
      {
        id: "amount_per_container",
        header: "Cant. x Cont.",
        value: (row) => row.amount_per_container ?? "-",
        pdfCellWidth: 14,
      },
      {
        id: "saldo",
        header: "Saldo",
        value: getHistoricalStockBalance,
        pdfCellWidth: 10,
      },
      {
        id: "unit",
        header: "UM",
        value: (row) => row.article?.unit?.description ?? "-",
        pdfCellWidth: 13,
      },
    );

    return columns;
  }, [filterVisibility, isClientUser]);

  const getExportHeader = () => {
    const { clientsLabel } = buildExcelHeader(
      clients,
      { clientIds: queryClientIds },
      userData,
    );
    const formattedStockDate = dayjs(stockDate).format("DD/MM/YYYY");

    return {
      clientsLabel,
      formattedStockDate,
      headerRows: [
        ["Stock al:", formattedStockDate],
        ["CLIENTES:", clientsLabel],
        [],
      ],
    };
  };

  const handleExportRequest = (format: "excel" | "pdf") => {
    if (exportLoading) return;
    if (isClientUser) {
      void handleExport("none", format);
      return;
    }
    setPendingExportFormat(format);
    setExportModalOpen(true);
  };

  const closeExportModal = () => {
    if (exportLoading) return;
    setExportModalOpen(false);
    setPendingExportFormat(null);
  };

  const handleExport = async (
    groupBy: StockExportGroupBy,
    format: "excel" | "pdf" | null = pendingExportFormat,
  ) => {
    if (!format || exportLoading) return;

    const separator = query.includes("?") ? "&" : "?";
    const exportQuery = `${query}${separator}groupBy=${groupBy}&_exportTimestamp=${Date.now()}`;

    try {
      setExportLoading(true);
      const response = await axiosGet(
        `${ARTICLE_LOCATIONS_BY_DATE_EXPORT_URL}${exportQuery}`,
        true,
      );

      if (response.status !== STATUS_CODES.OK) {
        throw new Error(
          response.data?.message ??
            "No se pudo obtener el stock por fecha para exportar.",
        );
      }

      const exportData =
        response.data as ArticleLocationStockByDateExportResponse;
      const { clientsLabel, formattedStockDate, headerRows } =
        getExportHeader();

      if (format === "excel") {
        if (groupBy === "none") {
          exportStockToExcel({
            rows: exportData.rows ?? [],
            columns: exportColumns,
            sheetName: "Stock por Fecha",
            fileNamePrefix: "Stock_por_Fecha",
            headerRows,
          });
        } else {
          exportGroupedStockToExcel({
            sections: exportData.sections ?? [],
            columns: exportColumns,
            groupBy,
            sheetName: "Stock por Fecha",
            fileNamePrefix: "Stock_por_Fecha",
            headerRows,
          });
        }
      } else {
        const subtitle = `Fecha de stock: ${formattedStockDate} | Clientes: ${clientsLabel}`;

        if (groupBy === "none") {
          exportStockToPdf({
            rows: exportData.rows ?? [],
            columns: exportColumns,
            title: "Listado de Stock por Fecha",
            subtitle,
            fileNamePrefix: "Stock_por_Fecha",
            tableFontSize: 6.5,
            tableCellPadding: 1,
          });
        } else {
          exportGroupedStockToPdf({
            sections: exportData.sections ?? [],
            columns: exportColumns,
            groupBy,
            title: "Listado de Stock por Fecha",
            subtitle,
            fileNamePrefix: "Stock_por_Fecha",
            tableFontSize: 6.5,
            tableCellPadding: 1,
          });
        }
      }

      setExportModalOpen(false);
      setPendingExportFormat(null);
    } catch (error) {
      console.error(error);
      showSnackBar(
        error instanceof Error
          ? error.message
          : "No se pudo preparar el stock por fecha.",
        "error",
      );
    } finally {
      setExportLoading(false);
    }
  };

  const handleLocationToChange = (value: LocationOption | null) => {
    if (!value || !filters.locationFrom) return;

    const storeLocations = locations;
    const fromIndex = storeLocations.findIndex(
      (location) => location.id === filters.locationFrom?.id,
    );
    const toIndex = storeLocations.findIndex(
      (location) => location.id === value.id,
    );
    if (fromIndex === -1 || toIndex === -1) return;

    const [start, end] =
      fromIndex <= toIndex ? [fromIndex, toIndex] : [toIndex, fromIndex];

    setFilters((current) => ({
      ...current,
      locationTo: value,
      locationIds: storeLocations
        .slice(start, end + 1)
        .map((location) => location.id),
    }));
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper elevation={4} sx={{ p: 2 }}>
          <Typography variant="h6">Filtros</Typography>
          <Grid container columnSpacing={2} rowSpacing={1} my={1}>
            <FilterItem>
              <DatePicker
                label="Fecha de stock"
                value={stockDate}
                maxDate={new Date()}
                onChange={(value) => {
                  if (value && !Number.isNaN(value.getTime())) {
                    setStockDate(value);
                  }
                }}
                disabled={loading}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </FilterItem>

            {!isClientUser && (
              <FilterItem>
                <Autocomplete
                  multiple
                  options={[
                    { id: 0, label: "Todos" },
                    ...stores
                      .filter((store) => store.is_active)
                      .map((store) => ({
                        id: store.id,
                        label: store.description,
                      })),
                  ]}
                  onChange={(_, value) => {
                    const storeIds = value.some((option) => option.id === 0)
                      ? stores
                          .filter((store) => store.is_active)
                          .map((store) => store.id)
                      : value.map((option) => option.id);
                    setFilters((current) => ({
                      ...current,
                      storeIds,
                      locationFrom: undefined,
                      locationTo: undefined,
                      locationIds: [],
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Depósitos" />
                  )}
                  disabled={loading}
                />
              </FilterItem>
            )}

            {!isClientUser && (
              <FilterItem>
                <Autocomplete
                  disabled={!filters.storeIds.length || loading}
                  options={locations
                    .map((location) => ({
                      id: location.id,
                      label: location.code,
                    }))}
                  onChange={(_, value) =>
                    setFilters((current) => ({
                      ...current,
                      locationFrom: value ?? undefined,
                      locationTo: undefined,
                      locationIds: [],
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Ubicación Desde" />
                  )}
                />
              </FilterItem>
            )}

            {!isClientUser && (
              <FilterItem>
                <Autocomplete
                  disabled={!filters.locationFrom || loading}
                  options={locations
                    .map((location) => ({
                      id: location.id,
                      label: location.code,
                    }))}
                  onChange={(_, value) => handleLocationToChange(value)}
                  renderInput={(params) => (
                    <TextField {...params} label="Ubicación Hasta" />
                  )}
                />
              </FilterItem>
            )}

            {!isClientUser && (
              <FilterItem>
                <Autocomplete
                  multiple
                  options={clients
                    .filter((client) => client.parent_client_id == null)
                    .map((client) => ({
                      ...client,
                      label: `${client.code} - ${client.razon_social}`,
                    }))}
                  onChange={(_, value) => {
                    setSelectedClients(value);
                    setSelectedLineClients([]);
                    setFilters((current) => ({
                      ...current,
                      clientIds: value.map((client) => client.id),
                      lineIds: [],
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Cliente" />
                  )}
                  disabled={loading}
                />
              </FilterItem>
            )}

            {(!isClientUser || lineOptions.length > 0) && (
              <FilterItem>
                <Autocomplete
                  multiple
                  options={lineOptions}
                  value={selectedLineClients}
                  getOptionLabel={(option) =>
                    `${option.code} - ${option.razon_social}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  onChange={(_, value) => {
                    setSelectedLineClients(value);
                    setFilters((current) => ({
                      ...current,
                      lineIds: value.map((client) => client.id),
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Líneas" />
                  )}
                  disabled={
                    (!isClientUser && !filters.clientIds.length) ||
                    !lineOptions.length ||
                    loading
                  }
                />
              </FilterItem>
            )}

            <FilterItem>
              <Autocomplete
                options={groupOptions}
                getOptionLabel={(option) => option.description ?? ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) =>
                  setFilters((current) => ({
                    ...current,
                    groupId: value?.id,
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Grupo de artículos" />
                )}
                disabled={loading}
              />
            </FilterItem>

            <FilterItem>
              <Autocomplete
                options={uniqueArticles}
                value={
                  uniqueArticles.find(
                    (article) => article.code === filters.articleCode,
                  ) ?? null
                }
                getOptionLabel={(option) => option.code ?? ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) =>
                  setFilters((current) => ({
                    ...current,
                    articleCode: value?.code ?? "",
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Artículo" />
                )}
                disabled={loading}
              />
            </FilterItem>

            <FilterItem>
              <Autocomplete
                options={filterOptions.amounts_per_container.map((value) => ({
                  label: String(value),
                }))}
                onChange={(_, value) =>
                  setFilters((current) => ({
                    ...current,
                    amount_per_container: value?.label ?? "",
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Cant. x Contenedor" />
                )}
                disabled={loading}
              />
            </FilterItem>

            {filterVisibility.show_lote && (
              <FilterItem>
                <Autocomplete
                  options={filterOptions.lotes.map((value) => ({ label: value }))}
                  onChange={(_, value) =>
                    setFilters((current) => ({
                      ...current,
                      lote: value?.label ?? "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Lote" />
                  )}
                  disabled={loading}
                />
              </FilterItem>
            )}

            {filterVisibility.show_oc && (
              <FilterItem>
                <Autocomplete
                  options={filterOptions.ocs.map((value) => ({ label: value }))}
                  onChange={(_, value) =>
                    setFilters((current) => ({
                      ...current,
                      oc: value?.label ?? "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="OC" />
                  )}
                  disabled={loading}
                />
              </FilterItem>
            )}

            {filterVisibility.show_fecha_vencimiento && (
              <FilterItem>
                <Autocomplete
                  options={filterOptions.expiration_dates.map((value) => ({
                    label: value,
                  }))}
                  onChange={(_, value) =>
                    setFilters((current) => ({
                      ...current,
                      fecha_vencimiento: value?.label ?? "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Fecha Vencimiento" />
                  )}
                  disabled={loading}
                />
              </FilterItem>
            )}

            <FilterItem>
              <Autocomplete
                options={filterOptions.income_dates.map((value) => ({
                  label: value,
                }))}
                onChange={(_, value) =>
                  setFilters((current) => ({
                    ...current,
                    fecha_ingreso: value?.label ?? "",
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Fecha Ingreso" />
                )}
                disabled={loading}
              />
            </FilterItem>

            <FilterItem>
              <TextField
                label="Número de Ingreso"
                fullWidth
                value={movementIdInput}
                onChange={(event) => setMovementIdInput(event.target.value)}
                onBlur={() =>
                  setFilters((current) => ({
                    ...current,
                    movementId: movementIdInput.trim(),
                  }))
                }
                disabled={loading}
              />
            </FilterItem>

            <FilterItem>
              <TextField
                label="Descripción de Artículo"
                fullWidth
                value={articleDescriptionInput}
                onChange={(event) =>
                  setArticleDescriptionInput(event.target.value)
                }
                onBlur={() =>
                  setFilters((current) => ({
                    ...current,
                    articleDescription: articleDescriptionInput.trim(),
                  }))
                }
                disabled={loading}
              />
            </FilterItem>
          </Grid>

          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Stock por Fecha</Typography>

            <Grid item>
              <Grid container spacing={2}>
                <Grid item>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<FaFileDownload />}
                    onClick={() => handleExportRequest("excel")}
                    disabled={!rows.length || loading || exportLoading}
                  >
                    Exportar Excel
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<FaFileDownload />}
                    onClick={() => handleExportRequest("pdf")}
                    disabled={!rows.length || loading || exportLoading}
                  >
                    Exportar PDF
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {loading ? (
            <Grid item xs={12} display="flex" justifyContent="center" my={6}>
              <CircularProgress />
            </Grid>
          ) : (
            <CustomGrid<ArticleLocationStockByDate>
              structure={structure}
              rows={rows}
              selectionModel={selectionModel}
              onRowSelection={setSelectionModel}
              checkboxSelection={false}
              getRowId={(row) => row.id}
              hideFooter
            />
          )}

          <Grid container justifyContent="center" mt={2}>
            <PaginationBar
              pageSize={PAGE_SIZE}
              currentPage={page}
              totalCount={totalCount}
              isDisabled={loading || rows.length === 0}
              onPageChange={setPage}
            />
          </Grid>

          {!loading && rows.length === 0 && (
            <Alert severity="info">
              <Typography variant="body2">
                No hay resultados disponibles
              </Typography>
            </Alert>
          )}
        </Paper>
      </Grid>
      <StockExportGroupingModal
        open={!isClientUser && exportModalOpen}
        loading={exportLoading}
        allowUngrouped
        onClose={closeExportModal}
        onConfirm={handleExport}
      />
      <Loader isFirstLoad={false} isLoading={exportLoading} />
    </Grid>
  );
}
